import { useVenue } from 'Shared/VenueProvider';
import { settingsRulesCategory } from 'Shared/frontend/styles/standardStyles';
import {createContext,useContext,onCleanup,mergeProps} from 'solid-js';
import { resolveStyleDetails } from 'Shared/frontend/widgets/Styles';

export interface PathElement {
	key: string|number;
	classes?: string[];
	childKey?: string|number;
}

export const ChildKeyContext = createContext<() => string|number|undefined>(() => undefined);

export function useChildKey() {
	return useContext(ChildKeyContext);
}

interface IStylesContent {
	path: PathElement[]|null;
	styleDetails: ()=>IStyleDetails[];
	setStyleDetails?: (details:IStyleDetails[])=>void,
	atTop?: () => boolean,

	/* Just used by OuterBasic.tsx */
	deferredClasses?: ()=>string[],
	setDeferredClasses?: ((func:()=>string[]) => void)
};

export interface IStyleDetails {
	category: string;
	tag: string;
	pathWhenAdded: (string|number)[];
	params?: any;
	rules: {
		location: string[];
		settings: XXX,
		css: YYY
	}[];
};


const StylesContext = createContext<IStylesContent>();

export function StylesProvider(P:{children} & IStylesContent)
{
	/* Nested providers set their own path and styleDetails but inherit the rest - notably atTop,
	   which only the top-level provider in OuterPage tracks. */
	const value = mergeProps(useContext(StylesContext) ?? {}, P);

	return (
		<StylesContext.Provider value={value}>
			{P.children}
		</StylesContext.Provider>
	);
}

export function useStyleDetails(): (()=>IStyleDetails[])
{
	return useContext(StylesContext)?.styleDetails ?? (() => []);
}

export function useStylePath(): PathElement[]
{
	return useContext(StylesContext)?.path ?? [];
}

export function useAtTop(): (() => boolean)
{
	return useContext(StylesContext)?.atTop ?? (() => false);
}

export function initPageStyles(data)
{
	const ctx = useContext(StylesContext);

	const frontend = useVenue().settings.frontend;

	const trimmedParentStyles = (frontend.styles ?? []).filter(e =>
		!(data.removeStyles ?? []).some((r: any) => r.category === (e as any).category && r.type === (e as any).type)
	);

	/* The nav and footer render outside (or around) any page, so their style lists ride every page
	   here alongside the site-wide styles. ShowWrapElement filters them back out by location -
	   ['nav','desktop'], ['nav','mobile'], ['nav','more'] and ['footer'].

	   Each node's own rules ride as a tag of their own, ahead of the styles the node has turned on,
	   so a style the venue picked deliberately - 'Fade in background' and the like - lands on top of
	   what its rules say. See settingsRulesCategory in standardStyles.ts. The sub-nav row has neither
	   list: its buttons are dressed by the venue style the innermost 'Buttons > Sub nav button style'
	   tag on this chain names - see subNavButtonCss(). */
	const wrapElementStyleTags = [
		{category: settingsRulesCategory, type: 'desktop'},
		{category: settingsRulesCategory, type: 'mobile'},
		{category: settingsRulesCategory, type: 'more'},
		{category: settingsRulesCategory, type: 'footer'},
		...(frontend.nav.desktop.styles ?? []),
		...(frontend.nav.mobile.styles ?? []),
		...(frontend.nav.mobile.more.styles ?? []),
		...(frontend.footer.styles ?? []),
	];

	const combinedStyleTags = [...trimmedParentStyles, ...wrapElementStyleTags, ...(data.styles ?? [])];
	const styleDetails = resolveStyleDetails([],combinedStyleTags);

	ctx.setStyleDetails(styleDetails);
	ctx.setDeferredClasses(data.classes);

	onCleanup(() => {
		ctx.setStyleDetails([]);
		ctx.setDeferredClasses([]);
	});
}

/* Just used by OuterBasic.tsx */
export function useDeferredClasses(): (()=>string[])
{
	return useContext(StylesContext).deferredClasses;
}


